import type { Language, Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Heading,
  IconEducation,
  IconGlobe,
  IconLocation,
  IconPersonVerified,
  Inline,
  Stack,
  Strong,
  Text,
  TooltipRenderer,
  useResponsiveValue,
} from 'braid-design-system';
import type { FC, ReactElement, ReactNode } from 'react';

import { ContactInfo } from 'src/App/AppPageBlock/ProfileDrawer/Profile/ContactInfo/ContactInfo';
import { StarRatingInput } from 'src/App/AppPageBlock/ProfileDrawer/Profile/StarRatingInput';
import { CertsyIdentityVerified } from 'src/App/CandidateManagementPage/JobApplicationCard/CertsyIdentityVerified';
import { convertLocationsIntoCommaList } from 'src/components/app/JobSection/utils';
import { UpgradeReferencesButton } from 'src/libs/ReferenceChecks/UpgradeReferencesButton';
import type { BUCKETS } from 'src/stores/JobApplicationStore/types';

import { ApplicationBadgesInOrder } from '../../../ApplicationBadgesInOrder';
import type { CheckReferencesActionOriginsProfileDrawer } from '../../createPageAnalytics';
import { AppliedDate } from '../AppliedDate';

import translations from './.vocab';
import { ProfileHeaderLayout } from './ProfileHeaderLayout';

interface ProfileHeaderProps {
  candidateId: string;
  jobApplicationId: string;
  fullName: string;
  certsyVerifiedFullName?: string;
  role: {
    mostRecentJobTitle?: string;
    mostRecentCompanyName?: string;
  };
  email: string;
  phone: string;
  verificationsCount: number;
  shouldShowRelevantApplicationBadge: boolean;
  shouldShowNewBadge: boolean;
  applicationStatus: BUCKETS;
  homeLocation?: string;
  nationalities?: string[];
  education?: {
    name: string;
    institute: string;
    completionYear?: number;
  };
  appliedDate: string;
  source: number;
  rating: number;
  verifications: string[];
  onClickRating: (index: number) => number;
  locale: Locale;
  language: Language;
  changeStatusButtons: ReactElement;
  referenceChecks:
    | {
        case: 'REQUESTED';
        type: 'LIMITED' | 'STANDARD';
      }
    | {
        case: 'LIMITED_AVAILABLE';
        canUnlockStandardReferenceCheck: boolean;
        onClick: (props: {
          actionOrigin: CheckReferencesActionOriginsProfileDrawer;
        }) => void;
        counts: {
          finishedCount: number;
          totalCount: number;
        };
      }
    | {
        case: 'STANDARD_AVAILABLE';
        counts: {
          finishedCount: number;
          totalCount: number;
        };
      }
    | {
        case: 'NOT_REQUESTED';
        showButton: true;
        onClick: () => void;
      }
    | {
        case: 'NOT_REQUESTED';
        showButton: false;
      };
}

export const ProfileHeader: FC<ProfileHeaderProps> = ({
  candidateId,
  jobApplicationId,
  fullName,
  certsyVerifiedFullName,
  role: { mostRecentJobTitle, mostRecentCompanyName },
  email,
  phone,
  verificationsCount,
  shouldShowRelevantApplicationBadge,
  shouldShowNewBadge,
  applicationStatus,
  homeLocation,
  nationalities,
  education,
  appliedDate,
  source,
  rating,
  verifications,
  onClickRating,
  locale,
  language,
  changeStatusButtons,
  referenceChecks,
}) => {
  const { t } = useTranslations(translations);
  const responsiveValue = useResponsiveValue();

  const shouldShowHomeLocation = homeLocation && homeLocation !== '';
  const shouldShowNationalities = nationalities && nationalities.length > 0;
  const shouldShowEducation = education && education.name !== '';
  const shouldShowKeyInfo =
    shouldShowHomeLocation || shouldShowNationalities || shouldShowEducation;

  const mostRecentRole = (): ReactNode => {
    if (!mostRecentJobTitle) {
      return <Strong>{t('New to workforce')}</Strong>;
    }

    if (!mostRecentCompanyName) {
      return <Strong>{mostRecentJobTitle}</Strong>;
    }

    return t('mostRecentJobTitle at mostRecentCompanyName', {
      Strong: (children) => <Strong>{children}</Strong>,
      mostRecentJobTitle,
      mostRecentCompanyName,
    });
  };

  return (
    <ProfileHeaderLayout
      applicantName={
        <Heading level="2">
          {fullName}{' '}
          {certsyVerifiedFullName ? (
            <CertsyIdentityVerified
              idSuffix={jobApplicationId}
              certsyVerifiedFullName={certsyVerifiedFullName}
              boxProps={{
                display: 'inline',
              }}
              iconSvgProps={
                responsiveValue({
                  mobile: {
                    width: '19px',
                    height: '19px',
                  },
                  tablet: {
                    width: '21px',
                    height: '21px',
                  },
                }) ?? { width: '21px', height: '21px' }
              }
            />
          ) : null}
        </Heading>
      }
      recentRole={<Text size="small">{mostRecentRole()}</Text>}
      contactInfo={
        <ContactInfo email={email} phone={phone} candidateId={candidateId} />
      }
      badges={
        <ApplicationBadgesInOrder
          shouldShowNewBadge={shouldShowNewBadge}
          shouldShowRelevantApplicationBadge={
            shouldShowRelevantApplicationBadge
          }
          referenceChecks={
            referenceChecks.case !== 'NOT_REQUESTED' ? referenceChecks : null
          }
          verificationsCount={verificationsCount}
          verifications={verifications}
          jobApplicationId={jobApplicationId}
          applicationStatus={applicationStatus}
          language={language}
          shouldShowPriorityApplyBadge={false}
          shouldShowHighFitBadge={false}
          applicationName={fullName}
        />
      }
      keyInformation={
        shouldShowKeyInfo ? (
          <Stack space="small">
            {shouldShowHomeLocation ? (
              <Text size="small" icon={<IconLocation />}>
                {homeLocation}
              </Text>
            ) : null}
            {shouldShowNationalities ? (
              <Text size="small" icon={<IconGlobe />}>
                {nationalities &&
                  convertLocationsIntoCommaList(nationalities, language)}
              </Text>
            ) : null}
            {shouldShowEducation ? (
              <TooltipRenderer
                id={`${candidateId}-education-tooltip`}
                tooltip={<Text size="small">{education!.institute}</Text>}
              >
                {({ triggerProps }) => (
                  <Box maxWidth="content" {...triggerProps}>
                    <Text size="small" icon={<IconEducation />}>
                      {education!.completionYear
                        ? `${education?.name}, ${education?.completionYear}`
                        : education?.name}
                    </Text>
                  </Box>
                )}
              </TooltipRenderer>
            ) : null}
          </Stack>
        ) : null
      }
      starRatingInput={
        <StarRatingInput rating={rating} onClick={onClickRating} />
      }
      appliedDate={
        <AppliedDate
          appliedDate={appliedDate}
          source={source}
          locale={locale}
        />
      }
      actionButtons={
        <Inline space="small" alignY="center">
          {referenceChecks.case === 'NOT_REQUESTED' &&
          referenceChecks.showButton ? (
            <Button
              variant="soft"
              size="small"
              icon={<IconPersonVerified />}
              onClick={(e) => {
                e.stopPropagation();
                referenceChecks.onClick();
              }}
            >
              {t('Check references')}
            </Button>
          ) : null}
          {referenceChecks.case === 'LIMITED_AVAILABLE' ? (
            <UpgradeReferencesButton
              onClick={() =>
                referenceChecks.onClick({
                  actionOrigin: 'candidate profile reference check pressed',
                })
              }
            />
          ) : null}
          {changeStatusButtons}
        </Inline>
      }
    />
  );
};
